import type { TOC } from '@ember/component/template-only';
import { concat, fn } from '@ember/helper';
import {
  PrismButton,
  PrismLoadingBar,
  PrismSelectInput,
  PrismTable,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { pageTitle } from 'ember-page-title';
import GeneralDropdownsDropdownExport from 'lumen/components/general/dropdowns/dropdown-export';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import HotelBudgetForecastExportModal from 'lumen/components/hotel/budget-forecast/export-modal';
import HotelBudgetForecastTableLayoutSettings from 'lumen/components/hotel/budget-forecast/table/layout-settings';
import HotelBudgetForecastVarianceOptions from 'lumen/components/hotel/budget-forecast/variance-options';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import SettingsBudgetForecastDatepicker from 'lumen/components/settings/budget-forecast/datepicker';
import type HotelForecastController from 'lumen/controllers/hotel/forecast';
import contextualPageTitle from 'lumen/helpers/contextual-page-title';
import hasFeature from 'lumen/helpers/has-feature';

import styles from './budget.module.css';

interface HotelForecastSignature {
  Args: {
    controller: HotelForecastController;
    model: unknown;
  };
}

<template>
  {{pageTitle (contextualPageTitle (t "menu.forecast"))}}
  <GeneralPageHeader>
    <div class="content-wrapper flex items-center gap-200">
      <div class="flex gap-400">
        <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
        <SettingsBudgetForecastDatepicker
          @endDate={{@controller.endDate}}
          @granularity={{@controller.granularity}}
          @handleDateRangeChange={{@controller.handleDateRangeChange}}
          @startDate={{@controller.startDate}}
        />
      </div>

      <PrismSelectInput
        @disabled={{@controller.isEntrySelectionDisabled}}
        @onChange={{@controller.updateSelectedEntry}}
        @options={{@controller.entryOptions}}
        @value={{@controller.selectedEntryOption}}
        data-test-select="forecast-entry"
      />

      <div class="ml-auto">
        <GeneralDropdownsDropdownExport
          @export={{@controller.handleExport}}
          @exportChoices={{@controller.exportChoices}}
          @isScheduleDisabled={{true}}
          data-test-id="budget-forecast-export-dropdown"
        />
      </div>
    </div>
  </GeneralPageHeader>

  {{#if @controller.isLoading}}
    <PrismLoadingBar @isFixed={{true}} @positionTop={{120}} />
  {{/if}}

  <GeneralPageMain>
    <div class="content-wrapper main-content flex h-full flex-col gap-800">
      <div
        class="flex items-center justify-between"
        data-test-id="forecast-header"
      >
        <h2>{{t "menu.forecast"}}</h2>

        <div class="flex gap-400">
          <HotelBudgetForecastTableLayoutSettings
            @baseDataType={{@controller.baseDataType}}
            @hiddenDataTypes={{@controller.layoutSettings.hiddenDataTypes}}
            @isDisabled={{@controller.isLoading}}
            @onConfirm={{@controller.updateTableLayoutSettings}}
            @pinnedDataTypes={{@controller.layoutSettings.pinnedDataTypes}}
            @unpinnedDataTypes={{@controller.layoutSettings.unpinnedDataTypes}}
          />
          <HotelBudgetForecastVarianceOptions
            @onChange={{fn (mut @controller.variance)}}
            @selectedVariance={{@controller.variance}}
          />

          {{#if
            (hasFeature "business_intelligence.settings.budget_and_forecast")
          }}
            <div class="border-neutral-default border-l border-solid"></div>

            <PrismButton
              @buttonType="primary"
              @iconAfter="arrow-right"
              @label={{t "budget_forecast.manage_forecasts"}}
              @onClick={{@controller.redirectToSettingsPage}}
              data-test-id="forecast-settings-redirect-button"
            />
          {{/if}}
        </div>
      </div>
      <div class={{concat styles.table-container " " "overflow-hidden"}}>
        <PrismTable
          @columns={{@controller.columns}}
          @footerRows={{@controller.footerRows}}
          @isLoading={{@controller.isLoading}}
          @onRowCollapseToggle={{@controller.onRowCollapseToggle}}
          @rows={{@controller.rows}}
          data-test-id="forecast-table"
        />
      </div>
    </div>
  </GeneralPageMain>

  {{#if @controller.selectedExportType}}
    <HotelBudgetForecastExportModal
      @dataTypes={{@controller.dataTypes}}
      @downloadCustomExport={{@controller.downloadCustomExport}}
      {{! @glint-expect-error: Incorrect type }}
      @entry={{@controller.selectedEntry}}
      @exportType={{@controller.selectedExportType}}
      @onClose={{@controller.onCloseExportModal}}
      @view="forecast"
    />
  {{/if}}
</template> satisfies TOC<HotelForecastSignature>;
